import { useEffect, useMemo, useState } from "react";
import { ChevronDown, ChevronRight, Search, X, Sparkles } from "lucide-react";
import { api } from "@/lib/api";
import Navbar from "@/components/Navbar";
import ProductCard from "@/components/ProductCard";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useLang, useT, categoryName } from "@/lib/i18n";

export default function Storefront() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [openMap, setOpenMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [onlyOffers, setOnlyOffers] = useState(false);
  const { lang } = useLang();
  const t = useT();

  useEffect(() => {
    Promise.all([api.get("/categories"), api.get("/products/public")])
      .then(([c, p]) => {
        setCategories(c.data || []);
        setProducts(p.data || []);
        const init = {};
        (c.data || []).forEach((cat) => (init[cat.id] = true));
        setOpenMap(init);
      })
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return products.filter((p) => {
      if (categoryFilter !== "all" && p.category_id !== categoryFilter) return false;
      if (onlyOffers && !p.offer) return false;
      if (q) {
        const hit = p.name.toLowerCase().includes(q) ||
                    (p.name_ta || "").toLowerCase().includes(q);
        if (!hit) return false;
      }
      return true;
    });
  }, [products, search, categoryFilter, onlyOffers]);

  const grouped = useMemo(() => {
    const g = {};
    categories.forEach((c) => (g[c.id] = { category: c, items: [] }));
    filtered.forEach((p) => {
      if (!g[p.category_id]) {
        g[p.category_id] = {
          category: { id: p.category_id, name: p.category_name || "Uncategorised" },
          items: [],
        };
      }
      g[p.category_id].items.push(p);
    });
    return Object.values(g).sort(
      (a, b) => (a.category.sort_order || 0) - (b.category.sort_order || 0)
    );
  }, [categories, filtered]);

  const hasActiveFilter = search || categoryFilter !== "all" || onlyOffers;
  const clearFilters = () => {
    setSearch("");
    setCategoryFilter("all");
    setOnlyOffers(false);
  };

  return (
    <div className="min-h-screen bg-bone">
      <Navbar />

      {/* Sticky filter bar under navbar */}
      <section
        className="sticky z-30 border-y border-black/5 backdrop-blur-xl bg-bone/95"
        style={{ top: "var(--nav-h, 105px)" }}
      >
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-3 flex flex-col md:flex-row md:items-center gap-3">
          <div className="flex-1 max-w-md">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-charcoal/40" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t("search_placeholder")}
                className="pl-10 bg-white border-black/10 focus-visible:ring-forest h-9"
                data-testid="storefront-search"
              />
            </div>
            <p
              className="mt-1.5 text-[11px] leading-snug text-red-500/80"
              data-testid="qty-hint-text"
            >
              {t("qty_hint")}
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={() => setCategoryFilter("all")}
              data-testid="filter-cat-all"
              className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                categoryFilter === "all"
                  ? "text-white border-transparent"
                  : "bg-white border-black/10 text-charcoal/70 hover:border-forest/40"
              }`}
              style={categoryFilter === "all" ? { backgroundColor: "var(--forest)" } : {}}
            >
              {t("all")}
            </button>
            {categories.map((c) => (
              <button
                key={c.id}
                onClick={() => setCategoryFilter(c.id)}
                data-testid={`filter-cat-${c.id}`}
                className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                  categoryFilter === c.id
                    ? "text-white border-transparent"
                    : "bg-white border-black/10 text-charcoal/70 hover:border-forest/40"
                }`}
                style={categoryFilter === c.id ? { backgroundColor: "var(--forest)" } : {}}
              >
                {categoryName(c, lang)}
              </button>
            ))}
            <button
              onClick={() => setOnlyOffers((v) => !v)}
              data-testid="filter-offers-toggle"
              className={`text-xs px-3 py-1.5 rounded-full inline-flex items-center gap-1.5 border transition-colors ${
                onlyOffers
                  ? "border-transparent text-white"
                  : "bg-white border-black/10 text-charcoal/70 hover:border-terracotta/40"
              }`}
              style={onlyOffers ? { backgroundColor: "var(--terracotta)" } : {}}
            >
              <Sparkles className="w-3 h-3" /> {t("only_offers")}
            </button>
            {hasActiveFilter && (
              <Button
                onClick={clearFilters}
                variant="ghost"
                size="sm"
                className="text-xs h-8 text-charcoal/60"
                data-testid="filter-clear"
              >
                <X className="w-3 h-3 mr-1" /> {t("clear")}
              </Button>
            )}
          </div>
        </div>
      </section>

      {/* Catalog — no hero carousel */}
      <main className="max-w-7xl mx-auto px-6 md:px-10 py-8 md:py-12">
        {loading ? (
          <div className="text-charcoal/60 py-24 text-center" data-testid="storefront-loading">
            {t("loading")}
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-charcoal/60 py-24 text-center" data-testid="storefront-empty">
            {hasActiveFilter ? t("empty_filtered") : t("empty_none")}
          </div>
        ) : (
          <div className="space-y-10">
            {grouped.map(({ category, items }) => {
              if (items.length === 0) return null;
              const open = openMap[category.id] !== false;
              return (
                <section key={category.id} data-testid={`category-section-${category.id}`}>
                  <button
                    onClick={() =>
                      setOpenMap((m) => ({ ...m, [category.id]: !open }))
                    }
                    className="w-full flex items-center justify-between py-3 border-b border-black/10 group"
                    data-testid={`category-toggle-${category.id}`}
                  >
                    <div className="flex items-baseline gap-4">
                      <span className="font-mono text-xs text-moss/80 uppercase tracking-[0.28em]">
                        {String(items.length).padStart(2, "0")} {t("items")}
                      </span>
                      <h2 className="font-serif text-3xl md:text-4xl font-bold text-forest">
                        {categoryName(category, lang)}
                      </h2>
                    </div>
                    <div className="text-forest/70 group-hover:text-forest transition-colors">
                      {open ? <ChevronDown className="w-6 h-6" /> : <ChevronRight className="w-6 h-6" />}
                    </div>
                  </button>
                  {open && (
                    <div
                      className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5 mt-6 fade-in-up"
                      data-testid={`product-grid-${category.id}`}
                    >
                      {items.map((p) => (
                        <ProductCard key={p.id} product={p} />
                      ))}
                    </div>
                  )}
                </section>
              );
            })}
          </div>
        )}
      </main>

      <footer className="border-t border-black/5 py-10 mt-10">
        <div className="max-w-7xl mx-auto px-6 md:px-10 flex flex-col md:flex-row justify-between items-center gap-4">
          <div className="text-sm text-charcoal/60">
            © {new Date().getFullYear()} Saravana Stores · {t("farmers_market")}. {t("footer_tagline")}
          </div>
          <a
            href="/admin/login"
            className="text-xs uppercase tracking-[0.24em] text-charcoal/50 hover:text-forest transition-colors"
            data-testid="footer-admin-link"
          >
            {t("footer_admin")}
          </a>
        </div>
      </footer>
    </div>
  );
}
